import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { ContractConcept } from '../../../../models/Entities/Contracts/ContractConcept';
import { ContractAddendumType } from '../../../../models/Entities/Contracts/NewContractAddendum';

// own functions
import * as ownFunctions from './ContractConceptSelector.Functions';
import { CheckmarkCircle20Filled } from '@fluentui/react-icons';
// La vigencia de una adenda se calcula por fechas, no por su bandera isActive.
// Se reutiliza la funcion del listado de adendas para no tener dos criterios
// distintos de "vigente" en la aplicacion.
import { isCurrentAddendum } from '../../../../components/Contracts/Addendums/Addendums.List/Addendums.Functions';
import {
  Field,
  Spinner,
  Tag,
  TagPicker,
  TagPickerControl,
  TagPickerGroup,
  TagPickerInput,
  TagPickerList,
  TagPickerOption,
  TagPickerOptionGroup,
  TagPickerProps,
  makeStyles,
  shorthands,
  tokens
} from '@fluentui/react-components';

const areStringArraysEqual = (left: string[], right: string[]) => {
  if (left.length !== right.length) return false;
  for (let i = 0; i < left.length; i++) {
    if (left[i] !== right[i]) return false;
  }
  return true;
};

/**
 * Fila de "sin resultados". TagPickerOption no admite `disabled`, y la lista
 * necesita al menos una opcion para que el popover tenga algo que mostrar: se
 * renderiza como opcion y se descarta al seleccionarla.
 */
const NO_OPTIONS_VALUE = '__no-options__';

const normalizeConceptName = (concept: ContractConcept) => (concept?.concept?.conceptName || '').trim().toUpperCase();

const getConceptDisplayName = (concept: ContractConcept) => (concept?.concept?.conceptName || '').trim();

const getConceptDescription = (concept: ContractConcept) => (concept?.concept?.conceptDescription || '').trim();

/**
 * Orden alfabetico con collation local: ignora mayusculas y acentos, y compara
 * los numeros por valor y no por caracter, para que "Adenda 2" vaya antes que
 * "Adenda 10".
 */
const compareLabels = (left: string, right: string) =>
  left.localeCompare(right, undefined, { numeric: true, sensitivity: 'base' });

/**
 * Texto sobre el que corre la busqueda. Incluye codigo y referencia externa
 * aunque no se muestren: son la forma en que muchos usuarios conocen al
 * concepto, y buscarlos y no encontrarlos se lee como que no existe.
 */
const buildSearchText = (concept: ContractConcept) =>
  [
    concept?.id,
    concept?.conceptId,
    concept?.concept?.conceptName,
    concept?.concept?.conceptDescription,
    concept?.concept?.conceptCode,
    concept?.concept?.conceptReferenceId
  ]
    .filter((part) => part !== undefined && part !== null && part !== '')
    .join(' ')
    .toLowerCase();

const useStyles = makeStyles({
  field: {
    maxWidth: '400px'
  },

  // Los slots `media` y `secondaryContent` de TagPickerOption colocan su
  // contenido con grid-row/grid-column, pero Option (react-combobox) fija
  // `display: flex` en la raiz y se mezcla despues del `display: grid` de
  // TagPickerOption, con lo que esas colocaciones nunca se aplican y los tres
  // textos caen en el mismo renglon. La fila se arma aca, sin depender de eso.
  option: {
    minWidth: 0,
    alignItems: 'center',
    ...shorthands.gap(tokens.spacingHorizontalS),
    ...shorthands.padding(tokens.spacingVerticalXS, tokens.spacingHorizontalS),
    // Todas las filas reservan el trazo lateral en transparente: si solo lo
    // llevara la seleccionada, al marcarse correria el contenido 3px.
    ...shorthands.borderLeft('3px', 'solid', 'transparent')
  },
  optionBody: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    flexGrow: 1
  },

  // El concepto ya asociado sigue en la lista (en seleccion simple hay que
  // poder volver a elegirlo). Un tilde suelto se pierde entre las filas, asi
  // que la marca se apoya en cuatro señales que se refuerzan: trazo lateral,
  // fondo de marca, pastilla de id invertida y tilde en circulo relleno.
  // El fondo neutro "selected" (#ebebeb) no servia: es casi el mismo gris que
  // el hover (#f5f5f5) y la fila no se distinguia.
  optionSelected: {
    backgroundColor: tokens.colorBrandBackground2,
    borderLeftColor: tokens.colorBrandStroke1
  },
  nameSelected: {
    color: tokens.colorBrandForeground1
  },
  idBadgeSelected: {
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand
  },
  optionCheck: {
    flexShrink: 0,
    fontSize: '20px',
    color: tokens.colorBrandForeground1
  },
  // El id va en pastilla y con cifras tabulares para que la columna quede
  // alineada y se lea como identificador, no como parte del nombre.
  idBadge: {
    display: 'inline-block',
    flexShrink: 0,
    minWidth: '2.5em',
    textAlign: 'center',
    ...shorthands.padding('1px', tokens.spacingHorizontalXS),
    ...shorthands.borderRadius(tokens.borderRadiusSmall),
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightSemibold,
    fontVariantNumeric: 'tabular-nums',
    lineHeight: tokens.lineHeightBase200,
    whiteSpace: 'nowrap'
  },
  // El nombre se muestra entero: es el dato con el que se elige, y recortarlo
  // obliga a abrir el tooltip para distinguir dos conceptos parecidos.
  name: {
    minWidth: 0,
    overflowWrap: 'anywhere',
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1
  },
  // La descripcion es contexto, no identidad: se queda en un solo renglon
  // debajo del nombre y recorta con ellipsis.
  description: {
    minWidth: 0,
    overflowX: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3
  },
  statusOption: {
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic'
  },

  // TagPicker mapea su tamaño "medium" a Tag "extra-small", que trae
  // height: 20px fijo. El renglon secundario de Tag necesita ~28px (16px del
  // primario + 14px del secundario - 2px de margen), asi que con la altura fija
  // la descripcion queda recortada. Se libera la altura en vez de agrandar todo
  // el control con size="extra-large".
  tag: {
    height: 'auto',
    minHeight: '20px',
    ...shorthands.padding('2px', '0')
  },

  // El tag vive dentro del control, que es angosto: nombre y descripcion
  // recortan para que un concepto de nombre largo no empuje al input de
  // busqueda fuera de la caja.
  tagPrimary: {
    maxWidth: '220px',
    overflowX: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  tagSecondary: {
    maxWidth: '220px',
    overflowX: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    color: tokens.colorNeutralForeground3
  },
  // Cabecera de adenda: se lee como separador, no como una opcion mas de la
  // lista, asi que va en minuscula tipografica y sin el peso de un item.
  groupLabel: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap(tokens.spacingHorizontalXS),
    ...shorthands.padding(tokens.spacingVerticalXS, tokens.spacingHorizontalS, '2px'),
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: tokens.colorNeutralForeground3
  },
  groupLabelText: {
    minWidth: 0,
    overflowX: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  // La adenda vigente se identifica con una pastilla verde: el orden es
  // alfabetico, asi que sin marca no habria forma de saber cual esta en curso.
  currentChip: {
    flexShrink: 0,
    ...shorthands.padding('0', tokens.spacingHorizontalXS),
    ...shorthands.borderRadius(tokens.borderRadiusCircular),
    ...shorthands.border('1px', 'solid', tokens.colorPaletteGreenBorder1),
    backgroundColor: tokens.colorPaletteGreenBackground1,
    color: tokens.colorPaletteGreenForeground1,
    letterSpacing: '0.03em',
    whiteSpace: 'nowrap'
  },
  loadingRow: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap(tokens.spacingHorizontalS),
    ...shorthands.padding(tokens.spacingVerticalXS, '0')
  }
});

const ContractConceptSelectorComponent = (props: ownFunctions.Props) => {
  const { t } = useTranslation();
  const styles = useStyles();

  useEffect(() => {
      props.GetContractById(props.ContractId);
  }, [props.ContractId]);

  useEffect(() => {
      if (props.Contract?.id) {
          props.GetAllContractsConcept(props.Contract.id, props.referralDate);
      }
  }, [props.Contract?.id, props.referralDate]);

  // Las adendas dan el nombre de cada grupo. El detalle del contrato no las
  // trae, por eso se piden aparte.
  useEffect(() => {
      if (props.Contract?.id) {
          props.GetContractAddendumsByContractId(props.Contract.id);
      }
  }, [props.Contract?.id]);

  const [internalContractsSelected, setInternalContractsSelected] = useState<ContractConcept[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<ContractConcept[]>([]);
  const [selectedOptionValues, setSelectedOptionValues] = useState<string[]>([]);
  const [query, setQuery] = useState<string>('');
  const requestedMissingConceptIdsRef = useRef<Set<string>>(new Set());

  const conceptsByName = useMemo(() => {
    const map = new Map<string, ContractConcept[]>();
    props.ContractConcepts.forEach((concept) => {
      const key = normalizeConceptName(concept);
      if (!key) {
        return;
      }

      const current = map.get(key) || [];
      current.push(concept);
      map.set(key, current);
    });

    return map;
  }, [props.ContractConcepts]);

  const distinctOptions = useMemo(() => {
    return Array.from(conceptsByName.entries()).map(([nameKey, concepts]) => ({
      nameKey,
      concept: concepts[0],
    }));
  }, [conceptsByName]);

  const selectedOptionsRef = useRef(selectedOptions);
  const selectedOptionValuesRef = useRef(selectedOptionValues);
  selectedOptionsRef.current = selectedOptions;
  selectedOptionValuesRef.current = selectedOptionValues;

  useEffect(() => {
    if (!props.ContractConceptsSelected || props.ContractConceptsSelected.length === 0) {
      setInternalContractsSelected([]);
      setSelectedOptions([]);
      setSelectedOptionValues([]);
      return;
    }

    const selectedValuesSet = new Set<string>();
    const selectedConcepts: ContractConcept[] = [];

    props.ContractConceptsSelected.forEach(selected => {
      if (selected === null || selected === undefined) {
        return;
      }

      if (typeof selected === 'number' || typeof selected === 'string') {
        const selectedId = selected.toString();
        const concept = props.ContractConcepts.find(c => c.id.toString() === selected.toString());
        if (concept) {
          if (props.DistinctByConceptName) {
            selectedValuesSet.add(normalizeConceptName(concept));
          } else {
            selectedValuesSet.add(selectedId);
            selectedConcepts.push(concept);
          }
        } else if (!props.DistinctByConceptName) {
          selectedValuesSet.add(selectedId);
        }
      } else if (typeof selected === 'object') {
        const selectedConcept = selected as ContractConcept;
        if (selectedConcept.id === null || selectedConcept.id === undefined) {
          return;
        }
        if (props.DistinctByConceptName) {
          selectedValuesSet.add(normalizeConceptName(selectedConcept));
        } else {
          selectedValuesSet.add(selectedConcept.id.toString());
          selectedConcepts.push(selectedConcept);
        }
      }
    });

    const selectedValues = Array.from(selectedValuesSet).filter((value) => !!value);

    if (props.DistinctByConceptName) {
      const selectedFromNameKeys = selectedValues.flatMap((value) => conceptsByName.get(value) || []);
      selectedConcepts.push(...selectedFromNameKeys);
    }

    const nextSelectedConceptIds = selectedConcepts.map((concept) => concept.id.toString());
    const currentSelectedConceptIds = selectedOptionsRef.current.map((concept) => concept.id.toString());

    if (!areStringArraysEqual(selectedOptionValuesRef.current, selectedValues)) {
      setSelectedOptionValues(selectedValues);
    }

    if (!areStringArraysEqual(currentSelectedConceptIds, nextSelectedConceptIds)) {
      setInternalContractsSelected(selectedConcepts);
      setSelectedOptions(selectedConcepts);
    }
  }, [props.ContractConceptsSelected, props.ContractConcepts, props.DistinctByConceptName, conceptsByName]);

  useEffect(() => {
    if (props.DistinctByConceptName) return;
    if (!selectedOptionValues.length) return;

    selectedOptionValues.forEach((value) => {
      const conceptExists = props.ContractConcepts.some((concept) => concept.id.toString() === value);
      if (conceptExists) {
        requestedMissingConceptIdsRef.current.delete(value);
      } else if (!requestedMissingConceptIdsRef.current.has(value)) {
        requestedMissingConceptIdsRef.current.add(value);
        const parsedId = Number(value);
        if (!Number.isNaN(parsedId)) {
          props.GetContractConceptById(parsedId);
        }
      }
    });
  }, [selectedOptionValues, props.ContractConcepts, props.DistinctByConceptName]);

  // Opciones disponibles: lo ya seleccionado se saca de la lista en modo
  // multiple, para no ofrecer dos veces lo mismo.
  const availableOptions: ContractConcept[] = useMemo(() => {
    const options = props.MultiselectEnable
      ? (props.DistinctByConceptName
        ? distinctOptions.filter((option) => !selectedOptionValues.includes(option.nameKey)).map((option) => option.concept)
        : props.ContractConcepts?.filter((concept) => !selectedOptionValues.includes(concept.id.toString())))
      : (props.DistinctByConceptName ? distinctOptions.map((option) => option.concept) : props.ContractConcepts);

    // Las adendas de negociacion son instancias de negociacion, no condiciones
    // vigentes del contrato: sus conceptos son propuestas y no corresponde
    // asociarles un consumo.
    const negotiationAddendumIds = new Set(
      props.Addendums
        .filter((addendum) => addendum.contractAddendumType === ContractAddendumType.NegociationInstance)
        .map((addendum) => addendum.id)
    );
    const knownAddendumIds = new Set(props.Addendums.map((addendum) => addendum.id));

    // Mientras las adendas no hayan cargado no se puede saber cual existe, y
    // filtrar contra un conjunto vacio dejaria la lista en blanco hasta que
    // resuelva el fetch. Recien con adendas cargadas se descarta.
    const canResolveAddendums = props.Addendums.length > 0;

    return (options || []).filter((option) => {
      if (!option || !option.concept) return false;
      if (!canResolveAddendums) return true;
      if (negotiationAddendumIds.has(option.contractAddendumId)) return false;
      // El concepto apunta a una adenda que no es de este contrato: no se
      // muestra. Ofrecerlo permitiria asociar un consumo a condiciones que no
      // pertenecen al contrato.
      return knownAddendumIds.has(option.contractAddendumId);
    });
  }, [props.MultiselectEnable, props.DistinctByConceptName, props.ContractConcepts, props.Addendums, distinctOptions, selectedOptionValues]);

  // Busqueda por terminos: todos tienen que aparecer, en cualquier orden y en
  // cualquiera de los campos. "biopsia 120" encuentra al concepto 120 aunque
  // el numero y la palabra no esten juntos.
  const searchTerms = useMemo(
    () => query.trim().toLowerCase().split(/\s+/).filter(Boolean),
    [query]
  );

  const tagPickerOptions: ContractConcept[] = useMemo(() => {
    const filtered = searchTerms.length === 0
      ? availableOptions
      : availableOptions.filter((option) => {
          const searchText = buildSearchText(option);
          return searchTerms.every((term) => searchText.includes(term));
        });

    // Copia antes de ordenar: availableOptions deriva de props y sort() muta.
    return [...filtered].sort((left, right) =>
      compareLabels(getConceptDisplayName(left), getConceptDisplayName(right))
    );
  }, [availableOptions, searchTerms]);

  const onQueryChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(event.target.value);
  }, []);

  /**
   * Agrupacion por adenda. Se arma sobre las opciones ya filtradas para que la
   * busqueda no deje cabeceras de grupos vacios.
   *
   * En modo distinto por nombre no se agrupa: ahi cada opcion representa a
   * varios conceptos que pueden vivir en adendas distintas, y meterla bajo una
   * sola cabecera afirmaria algo que no es cierto.
   */
  const optionGroups = useMemo(() => {
    if (props.DistinctByConceptName) return [];
    // Sin adendas cargadas no hay nombres que poner: se muestra la lista plana
    // hasta que resuelva el fetch, en lugar de cabeceras sin identificar.
    if (props.Addendums.length === 0) return [];

    const addendumsById = new Map(props.Addendums.map((addendum) => [addendum.id, addendum]));
    const groupsById = new Map<number, ContractConcept[]>();

    tagPickerOptions.forEach((option) => {
      const key = option.contractAddendumId;
      const current = groupsById.get(key) || [];
      current.push(option);
      groupsById.set(key, current);
    });

    return Array.from(groupsById.entries())
      .map(([addendumId, options]) => {
        const addendum = addendumsById.get(addendumId);
        const name = addendum?.contractAddendumName?.trim();

        // Llegado aca la adenda existe (las que no se resuelven ya quedaron
        // fuera de availableOptions); solo puede faltarle el nombre, y ahi el id
        // si es honesto porque identifica a una adenda que esta de verdad.
        const label = name || String(t('ContractConceptSelector.addendumUnnamed', 'Adenda #{{id}}', { id: addendumId }));

        return {
          addendumId,
          isCurrent: isCurrentAddendum(addendum),
          label,
          options
        };
      })
      .sort((left, right) => compareLabels(left.label, right.label));
  }, [props.DistinctByConceptName, props.Addendums, tagPickerOptions, t]);

  // Una sola adenda no necesita cabecera: seria un titulo para toda la lista.
  const shouldGroup = optionGroups.length > 1;

  const onOptionSelect: TagPickerProps['onOptionSelect'] = (event, data) => {
      // La fila de estado no es una opcion real: elegirla no cambia nada.
      if (data.value === NO_OPTIONS_VALUE) return;

      let selectedValues: string[] = [];
      if (props.MultiselectEnable) {
        selectedValues = data.selectedOptions;
      } else if (data.selectedOptions?.length) {
        const latestSelected = data.selectedOptions[data.selectedOptions.length - 1];
        selectedValues = latestSelected ? [latestSelected] : [];
      } else if (data.value) {
        const isAlreadySelected = selectedOptionValuesRef.current.length === 1 && selectedOptionValuesRef.current[0] === data.value;
        selectedValues = isAlreadySelected ? [] : [data.value];
      }

      const newSelectedOptions = props.DistinctByConceptName
        ? selectedValues.flatMap((value) => conceptsByName.get(value) || [])
        : selectedValues
          .map((value) => props.ContractConcepts.find(c => c.id.toString() === value))
          .filter((concept): concept is ContractConcept => concept !== undefined);

      // Elegido el concepto, el texto tecleado ya cumplio su funcion: si queda,
      // la proxima apertura muestra la lista filtrada por una busqueda vieja.
      setQuery('');
      setSelectedOptionValues(selectedValues);
      setInternalContractsSelected(newSelectedOptions);
      setSelectedOptions(newSelectedOptions);
      props.onSelectedContractConcept(newSelectedOptions);
  }

  const hasConcepts = availableOptions.length > 0;

  /** En modo distinto por nombre la identidad es el nombre: mostrar el id de un
   *  representante mentiria cuando el grupo tiene varios. Ahi se informa cuantos. */
  const renderOptionBadge = (option: ContractConcept) => {
    if (props.DistinctByConceptName) {
      const groupSize = (conceptsByName.get(normalizeConceptName(option)) || []).length;
      if (groupSize > 1) {
        return `${groupSize} ${t('ContractConceptSelector.variants', 'variantes')}`;
      }
    }
    return `#${option.id}`;
  };

  const renderOption = (option: ContractConcept) => {
    const optionValue = props.DistinctByConceptName ? normalizeConceptName(option) : option.id.toString();
    const name = getConceptDisplayName(option);
    const description = getConceptDescription(option);
    const isSelected = selectedOptionValues.includes(optionValue);

    return (
      <TagPickerOption
        className={isSelected ? `${styles.option} ${styles.optionSelected}` : styles.option}
        value={optionValue}
        key={optionValue}
        text={name}
      >
        <span className={isSelected ? `${styles.idBadge} ${styles.idBadgeSelected}` : styles.idBadge}>
          {renderOptionBadge(option)}
        </span>
        <span className={styles.optionBody}>
          <span className={isSelected ? `${styles.name} ${styles.nameSelected}` : styles.name}>{name}</span>
          {/* Sin descripcion no se emite el renglon: una fila de una linea vale
              mas que un renglon ocupado por un texto que no dice nada. */}
          {description && (
            <span className={styles.description} title={description}>{description}</span>
          )}
        </span>
        {isSelected && (
          <CheckmarkCircle20Filled
            className={styles.optionCheck}
            aria-label={t('ContractConceptSelector.selected', 'Seleccionado')}
          />
        )}
      </TagPickerOption>
    );
  };

  return (<>
      <Field
        className={styles.field}
        label={t('ContractConceptSelector.label', 'Select a concept')}
      >
        <TagPicker
          onOptionSelect={onOptionSelect}
          selectedOptions={selectedOptionValues}
        >
          <TagPickerControl>
            <TagPickerGroup aria-label={t('ContractConceptSelector.selectedAria', 'Selected concepts')}>
              {selectedOptionValues.map((optionValue) => {
                const selectedConcept = props.DistinctByConceptName
                  ? (conceptsByName.get(optionValue) || [])[0]
                  : props.ContractConcepts.find((option) => option.id.toString() === optionValue);
                const selectedName = selectedConcept ? getConceptDisplayName(selectedConcept) : optionValue;
                const selectedDescription = selectedConcept ? getConceptDescription(selectedConcept) : '';
                return (
                <Tag
                  key={optionValue}
                  className={selectedDescription ? styles.tag : undefined}
                  shape="rounded"
                  value={optionValue}
                  dismissible
                  // El tag repite lo mismo que la opcion de la lista: sin el id
                  // no se distinguen dos conceptos que comparten nombre, y sin
                  // la descripcion no se ve que se eligio hasta reabrir la lista.
                  media={selectedConcept
                    ? <span className={styles.idBadge}>{renderOptionBadge(selectedConcept)}</span>
                    : undefined}
                  primaryText={{ className: styles.tagPrimary, title: selectedName }}
                  secondaryText={selectedDescription
                    ? { className: styles.tagSecondary, children: selectedDescription, title: selectedDescription }
                    : undefined}
                >
                  {selectedName}
                </Tag>
                );
              })}
            </TagPickerGroup>
            <TagPickerInput
              aria-label={t('ContractConceptSelector.inputAria', 'Select a concept')}
              placeholder={
                props.isLoading
                  ? t('ContractConceptSelector.loading', 'Cargando conceptos…')
                  : !hasConcepts
                    ? t('ContractConceptSelector.noOptions', 'No hay conceptos disponibles')
                    : t('ContractConceptSelector.searchPlaceholder', 'Buscar por id, nombre o descripción…')
              }
              value={query}
              onChange={onQueryChange}
            />
          </TagPickerControl>
          <TagPickerList>
            {props.isLoading && !hasConcepts ? (
              <div className={styles.loadingRow}>
                <Spinner size="tiny" label={t('ContractConceptSelector.loading', 'Cargando conceptos…')} />
              </div>
            ) : tagPickerOptions.length > 0 ? (
              shouldGroup ? (
                optionGroups.map((group) => (
                  <TagPickerOptionGroup
                    key={group.addendumId}
                    label={{
                      className: styles.groupLabel,
                      title: group.label,
                      children: (
                        <>
                          <span className={styles.groupLabelText}>{group.label}</span>
                          {group.isCurrent && (
                            <span className={styles.currentChip}>
                              {t('ContractConceptSelector.addendumCurrent', 'Vigente')}
                            </span>
                          )}
                        </>
                      )
                    }}
                  >
                    {group.options.map(renderOption)}
                  </TagPickerOptionGroup>
                ))
              ) : (
                tagPickerOptions.map(renderOption)
              )
            ) : (
              <TagPickerOption
                className={styles.statusOption}
                value={NO_OPTIONS_VALUE}
                text=""
              >
                {searchTerms.length > 0
                  ? t('ContractConceptSelector.noMatches', 'Ningún concepto coincide con la búsqueda')
                  : t('ContractConceptSelector.noOptions', 'No hay conceptos disponibles')}
              </TagPickerOption>
            )}
          </TagPickerList>
        </TagPicker>
      </Field>
      {props.noteText && <small>{props.noteText}</small>}
  </>);
};

export default connect(
  ownFunctions.mapStateToProps,
  ownFunctions.mapDispatchToProps
)(ContractConceptSelectorComponent as any);
